import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2, Crown, Zap } from 'lucide-react';
import { Button } from './ui/button';

/**
 * SubscriptionTeaser
 * Two states:
 *  - If user has an active subscription → shows a compact "Your plan" card with quota usage.
 *  - Otherwise → shows a promo banner inviting to subscribe.
 */
export default function SubscriptionTeaser({ subscription, packagesCount = 0 }) {
  const active = subscription?.active;

  if (active && subscription?.plan) {
    const used = subscription.usage?.packages_used || 0;
    const quota = subscription.usage?.monthly_quota || 0;
    const pct = Math.min(100, quota > 0 ? (used / quota) * 100 : 0);
    return (
      <div className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 border-2 border-emerald-200 rounded-2xl p-5 md:p-6 shadow-sm" data-testid="sub-teaser-active">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs uppercase tracking-wider font-bold text-emerald-700">Votre abonnement</span>
                <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">ACTIF</span>
              </div>
              <h3 className="font-heading font-black text-xl md:text-2xl text-slate-900">
                {subscription.plan.name} — {used}/{quota} colis ce mois
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {subscription.cancel_at_period_end
                  ? '⚠️ Annulation programmée à la fin de la période'
                  : 'Frais de gestion offerts sur les colis inclus'}
              </p>
            </div>
          </div>
          <Link to="/abonnements">
            <Button variant="outline" data-testid="sub-teaser-manage">Gérer</Button>
          </Link>
        </div>
        <div className="mt-4">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
    );
  }

  // Non-subscribed: show promo
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 border border-blue-800 rounded-2xl p-5 md:p-6 shadow-lg" data-testid="sub-teaser-promo">
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        backgroundImage: 'radial-gradient(circle at 15% 40%, rgba(59,130,246,0.4), transparent 40%), radial-gradient(circle at 85% 60%, rgba(251,191,36,0.25), transparent 40%)',
      }} />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4 text-white">
          <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1 bg-amber-400/20 text-amber-300 border border-amber-400/40 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1">
              <Zap className="w-3 h-3" /> Offre exclusive
            </span>
            <h3 className="font-heading font-black text-xl md:text-2xl leading-tight mb-1">
              {packagesCount >= 2
                ? 'Vous envoyez souvent ? Économisez avec un abonnement'
                : 'Envoyez plus, payez moins avec un abonnement'}
            </h3>
            <p className="text-white/80 text-sm mb-2">
              À partir de <strong className="text-amber-300">19,99€/mois</strong> — frais de gestion offerts, priorité entrepôt, sans engagement.
            </p>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/90">
              <li className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 5 à 50 colis inclus/mois</li>
              <li className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Frais de gestion offerts</li>
              <li className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Annulation à tout moment</li>
            </ul>
          </div>
        </div>
        <Link to="/abonnements" className="flex-shrink-0">
          <Button className="bg-white text-blue-900 hover:bg-amber-300 hover:text-slate-900 font-bold shadow-xl group" data-testid="sub-teaser-cta">
            Découvrir les offres
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
